/**
 * Auto-dismissing toast.
 *
 * - `info` / `success` use `role="status"` (polite live region).
 * - `warn` / `error` use `role="alert"` (assertive — interrupts SR users).
 * - Default 4000ms timeout; pass `durationMs={0}` to disable auto-dismiss.
 */
import { useEffect, type CSSProperties } from 'react';

export type ToastVariant = 'info' | 'success' | 'warn' | 'error';

export interface ToastProps {
  open: boolean;
  message: string;
  variant?: ToastVariant;
  onClose: () => void;
  /** Auto-dismiss delay in ms (0 disables). Defaults to 4000. */
  durationMs?: number;
}

const VARIANT_STYLE: Record<ToastVariant, CSSProperties> = {
  info: {
    background: 'var(--mocha-700)',
    color: 'var(--cream-50)',
  },
  success: {
    background: 'var(--matcha-700)',
    color: 'var(--cream-50)',
  },
  warn: {
    background: 'var(--warn)',
    color: 'var(--cream-50)',
  },
  error: {
    background: 'var(--danger)',
    color: 'var(--cream-50)',
  },
};

export default function Toast({
  open,
  message,
  variant = 'info',
  onClose,
  durationMs = 4000,
}: ToastProps): JSX.Element | null {
  useEffect(() => {
    if (!open || durationMs <= 0) return;
    const t = window.setTimeout(onClose, durationMs);
    return () => window.clearTimeout(t);
  }, [open, durationMs, onClose]);

  if (!open) return null;

  const isAssertive = variant === 'warn' || variant === 'error';

  return (
    <div
      role={isAssertive ? 'alert' : 'status'}
      aria-live={isAssertive ? 'assertive' : 'polite'}
      aria-atomic="true"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 90px)',
        transform: 'translateX(-50%)',
        padding: '12px 18px',
        borderRadius: 'var(--radius-pill)',
        boxShadow: 'var(--shadow-2)',
        font: '500 13px var(--font-cjk)',
        maxWidth: 'min(560px, calc(100vw - 32px))',
        textAlign: 'center',
        zIndex: 200,
        ...VARIANT_STYLE[variant],
      }}
    >
      {message}
    </div>
  );
}
